/**
 * @prettier
 */

import createBehavior from '@js/functions/createBehavior.js';

const FCPLAYER_SCRIPT_SRC =
  'https://player.freecaster.com/freecaster/stable/fcplayer.js';

const SPOTIFY_API_SRC = 'https://open.spotify.com/embed/iframe-api/v1';

const modalAudioBehavior = createBehavior(
  'modal-audio',
  {
    bindUI() {
      this.ui = {};
      this.ui.close = this.getChild('close');
      this.ui.player = this.getChild('player');
      this.ui.cover = this.getChild('cover');
      this.ui.playBtn = this.getChild('play');
      this.ui.title = this.getChild('title');
      this.ui.description = this.getChild('description');
      this.ui.progress = this.getChild('progress');
      this.ui.prev = this.getChild('prev');
      this.ui.next = this.getChild('next');
      this.ui.seekFill = this.getChild('seek-fill');
      this.ui.seekRemaining = this.getChild('seek-remaining');
      this.ui.seekTrack = this.getChild('seek-track');
      this.ui.body = document.querySelector('body');

      this.remainingLabel =
        this.ui.seekRemaining?.dataset.i18nRemaining || 'min restantes';

      this.state = {
        audios: [],
        currentIndex: 0,
        isPlaying: false,
        player: null,
        loadToken: null,
        duration: 0,
        isSeeking: false,
      };
    },
    bindEvents() {
      this.ui.close.addEventListener('click', this.closeHandler, false);
      this.ui.prev.addEventListener('click', this.prevHandler, false);
      this.ui.next.addEventListener('click', this.nextHandler, false);
      this.ui.playBtn.addEventListener('click', this.togglePlay, false);
      this.ui.seekTrack.addEventListener('mousedown', this.onSeekStart, false);
      this.ui.seekTrack.addEventListener('touchstart', this.onSeekStart, {
        passive: true,
      });
      document.addEventListener('card-audio:open', this.onOpen, false);
      document.addEventListener('keydown', this.onKeydown, false);
    },
    loadGlobalScript() {
      if (document.querySelector(`script[src="${FCPLAYER_SCRIPT_SRC}"]`))
        return;
      const script = document.createElement('script');
      script.async = true;
      script.src = FCPLAYER_SCRIPT_SRC;
      document.head.appendChild(script);
    },
    loadSpotifyEmbed(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const iframe = tmp.querySelector('iframe');
      if (!iframe) {
        this.ui.player.innerHTML = html;
        return;
      }

      const match = iframe.src.match(
        /open\.spotify\.com\/embed\/([^/?]+)\/([^/?]+)/,
      );
      if (!match) {
        this.ui.player.innerHTML = html;
        return;
      }

      const uri = `spotify:${match[1]}:${match[2]}`;
      const container = document.createElement('div');
      this.ui.player.appendChild(container);

      const init = (IFrameAPI) => {
        IFrameAPI.createController(container, { uri }, (controller) => {
          this.state.spotifyController = controller;
          controller.resume();
        });
      };

      if (window.SpotifyIframeApiReady) {
        init(window.SpotifyIframeApiReady);
      } else {
        const prev = window.onSpotifyIframeApiReady;
        window.onSpotifyIframeApiReady = (api) => {
          window.SpotifyIframeApiReady = api;
          if (prev) prev(api);
          init(api);
        };
        const script = document.createElement('script');
        script.async = true;
        script.src = SPOTIFY_API_SRC;
        document.head.appendChild(script);
      }
    },
    onOpen(e) {
      this.state.audios = e.detail.audios;
      this.state.currentIndex = e.detail.index;

      this.renderProgress();
      this.loadAudio(this.state.currentIndex);
      this.updateNavVisibility();

      this.node.classList.add('is-visible');
      this.ui.body.classList.add('has-modal');
    },
    loadAudio(index) {
      const audioData = this.state.audios[index];
      if (!audioData) return;

      if (this.ui.title) {
        this.ui.title.textContent = audioData.title || '';
      }

      if (this.ui.description) {
        this.ui.description.textContent = audioData.description || '';
        this.ui.description.classList.toggle(
          'is-hidden',
          !audioData.description,
        );
      }

      if (this.ui.cover) {
        this.ui.cover.src = audioData.image || '';
        this.ui.cover.alt = audioData.title || '';
      }

      if (window.fbq) {
        fbq('trackCustom', 'AudioPlay', {
          content_name: audioData.title || '',
          content_id: audioData.audio_id || '',
        });
      }

      this.updateSeek(0, 0);
      this.destroyPlayer();

      if (audioData.type === 'freecaster' && audioData.audio_id) {
        this.node.classList.remove('is-embed');
        this.clearProviderClasses();
        this.loadFreecaster(audioData);
      } else if (audioData.embed) {
        this.node.classList.add('is-embed');
        this.clearProviderClasses();
        try {
          const html = atob(audioData.embed);
          const provider = this.detectEmbedProvider(html);
          if (provider) this.node.classList.add('is-embed--' + provider);
          if (provider === 'spotify') {
            this.loadSpotifyEmbed(html);
          } else {
            this.ui.player.innerHTML = this.injectAutoplay(html, provider);
          }
        } catch (err) {
          this.ui.player.innerHTML = '';
        }
      }

      this.renderProgress();
      this.updateNavVisibility();
    },
    loadFreecaster(audioData) {
      this.node.classList.add('is-loading');
      const loadToken = Date.now();
      this.state.loadToken = loadToken;

      // Unique ID avoids collision with window.fcplayer (browser exposes
      // elements with id="fcplayer" as window.fcplayer, shadowing the API)
      const containerId = 'fc-audio-' + loadToken;

      // _fcpr callback — fires for each player Freecaster initializes
      window._fcpr = window._fcpr || [];
      window._fcpr.push((player) => {
        if (this.state.loadToken !== loadToken || this.state.player) return;
        if (player.id !== containerId) return;
        this.setupPlayer(player);
      });

      // Player container with Freecaster data attributes
      const container = document.createElement('div');
      container.id = containerId;
      container.className = 'freecaster-player';
      container.dataset.videoId = audioData.audio_id;
      container.dataset.autoplay = 'true';
      this.ui.player.appendChild(container);

      // Fallback: poll window.fcplayer(id) after the global script is ready
      this.waitForPlayer(loadToken, containerId);
    },
    waitForPlayer(loadToken, containerId, attempts) {
      if (this.state.loadToken !== loadToken || this.state.player) return;

      const player =
        typeof window.fcplayer === 'function'
          ? window.fcplayer(containerId)
          : null;

      if (player) {
        this.setupPlayer(player);
      } else if ((attempts || 0) < 30) {
        setTimeout(
          () => this.waitForPlayer(loadToken, containerId, (attempts || 0) + 1),
          100,
        );
      }
    },
    setupPlayer(player) {
      this.node.classList.remove('is-loading');
      this.state.player = player;

      player.on('play', () => this.setPlaying(true));
      player.on('pause', () => this.setPlaying(false));
      player.on('ended', () => {
        this.setPlaying(false);
        this.updateSeek(0, 0);
      });
      player.on('timeupdate', () => {
        this.updateSeek(player.currentTime, player.duration);
      });

      // Play immediately since the modal was opened by a user gesture
      player.play();
      this.setPlaying(true);
    },
    clearProviderClasses() {
      this.node.classList.remove(
        'is-embed--apple',
        'is-embed--spotify',
        'is-embed--soundcloud',
      );
    },
    injectAutoplay(html, provider) {
      if (provider !== 'soundcloud') return html;

      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const iframe = tmp.querySelector('iframe');
      if (!iframe) return html;

      const url = new URL(iframe.src);
      url.searchParams.set('auto_play', 'true');
      iframe.src = url.toString();
      return tmp.innerHTML;
    },
    detectEmbedProvider(html) {
      if (/embed\.music\.apple\.com/.test(html)) return 'apple';
      if (/open\.spotify\.com\/embed/.test(html)) return 'spotify';
      if (/w\.soundcloud\.com\/player/.test(html)) return 'soundcloud';
      return null;
    },
    destroyPlayer() {
      this.state.loadToken = null;
      this.state.duration = 0;
      this.node.classList.remove('is-embed', 'is-loading');
      this.clearProviderClasses();

      if (this.state.player) {
        try {
          this.state.player.destroy();
        } catch (e) {}
        this.state.player = null;
      }

      if (this.state.spotifyController) {
        try {
          this.state.spotifyController.destroy();
        } catch (e) {}
        this.state.spotifyController = null;
      }

      if (this.ui.player) {
        this.ui.player.innerHTML = '';
      }
    },
    setPlaying(isPlaying) {
      this.state.isPlaying = isPlaying;
      this.node.classList.toggle('is-playing', isPlaying);
    },
    togglePlay() {
      if (!this.state.player) return;

      if (this.state.isPlaying) {
        this.state.player.pause();
      } else {
        this.state.player.play();
      }
    },
    updateSeek(position, duration) {
      if (!this.ui.seekFill || !this.ui.seekRemaining) return;

      if (duration > 0) this.state.duration = duration;

      if (!this.state.isSeeking) {
        const pct = duration > 0 ? (position / duration) * 100 : 0;
        this.ui.seekFill.style.width = pct + '%';
      }

      const remaining = duration > position ? duration - position : 0;
      this.ui.seekRemaining.textContent =
        remaining > 0
          ? this.formatTime(remaining) + ' ' + this.remainingLabel
          : '–:––';
    },
    onSeekStart(e) {
      if (!this.state.player || !this.state.duration) return;
      this.state.isSeeking = true;

      this.applySeek(e);

      const onMove = (ev) => this.applySeek(ev);
      const onEnd = (ev) => {
        this.applySeek(ev);
        this.state.isSeeking = false;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onEnd);
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('touchend', onEnd);
      };

      document.addEventListener('mousemove', onMove, false);
      document.addEventListener('mouseup', onEnd, false);
      document.addEventListener('touchmove', onMove, false);
      document.addEventListener('touchend', onEnd, false);
    },
    applySeek(e) {
      const track = this.ui.seekTrack;
      if (!track) return;

      const rect = track.getBoundingClientRect();
      const clientX = e.touches
        ? e.touches[0]?.clientX ?? e.changedTouches[0]?.clientX
        : e.clientX;
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));

      this.ui.seekFill.style.width = pct * 100 + '%';
      this.state.player.seek(pct * this.state.duration);
    },
    formatTime(seconds) {
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60);
      return m + ':' + (s < 10 ? '0' + s : s);
    },
    renderProgress() {
      const total = this.state.audios.length;
      if (!total || !this.ui.progress) return;

      let html = '';
      for (let i = 0; i < total; i++) {
        html +=
          '<span class="modal-audio__progress-item' +
          (i === this.state.currentIndex ? ' is-active' : '') +
          '"></span>';
      }
      this.ui.progress.innerHTML = html;
    },
    updateNavVisibility() {
      const total = this.state.audios.length;
      if (this.ui.prev) this.ui.prev.disabled = this.state.currentIndex === 0;
      if (this.ui.next)
        this.ui.next.disabled = this.state.currentIndex === total - 1;
    },
    prevHandler() {
      if (this.state.currentIndex > 0) {
        this.state.currentIndex--;
        this.loadAudio(this.state.currentIndex);
      }
    },
    nextHandler() {
      if (this.state.currentIndex < this.state.audios.length - 1) {
        this.state.currentIndex++;
        this.loadAudio(this.state.currentIndex);
      }
    },
    closeHandler() {
      this.destroyPlayer();
      this.setPlaying(false);
      this.node.classList.remove('is-visible');
      this.ui.body.classList.remove('has-modal');
    },
    onKeydown(e) {
      if (!this.node.classList.contains('is-visible')) return;
      if (e.key === 'Escape') this.closeHandler();
      if (e.key === 'ArrowLeft') this.prevHandler();
      if (e.key === 'ArrowRight') this.nextHandler();
      if (e.key === ' ') {
        e.preventDefault();
        this.togglePlay();
      }
    },
  },
  {
    init() {
      this.bindUI();
      this.bindEvents();
      this.loadGlobalScript();
    },
    destroy() {},
  },
);

export default modalAudioBehavior;
